import { debugLog } from '../utils/debug';
import { ApiService } from '../utils/ApiService';
import { TokenManager } from '../auth/TokenManager';
import { DeviceFingerprint } from '../utils/DeviceFingerprint';

export interface SyntaxConfig {
  rangeOperator: string;
  wildcardChar: string;
  addOperator: string;
  excludeOperator: string;
}

const DEFAULT_SYNTAX: SyntaxConfig = {
  rangeOperator: ':',
  wildcardChar: '*',
  addOperator: '+',
  excludeOperator: '-',
};

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export class SyntaxConfigurationService {
  private static instance: SyntaxConfigurationService;
  private config: SyntaxConfig = { ...DEFAULT_SYNTAX };
  private initialized = false;
  private lastFetchTime = 0;
  private apiService: ApiService;
  private tokenManager: TokenManager;
  private deviceFingerprint: DeviceFingerprint;

  private constructor() {
    this.apiService = new ApiService();
    this.tokenManager = new TokenManager();
    this.deviceFingerprint = new DeviceFingerprint();
  }

  static getInstance(): SyntaxConfigurationService {
    if (!SyntaxConfigurationService.instance) {
      SyntaxConfigurationService.instance = new SyntaxConfigurationService();
    }
    return SyntaxConfigurationService.instance;
  }

  async initialize(): Promise<void> {
    if (this.initialized && Date.now() - this.lastFetchTime < CACHE_TTL_MS) {
      return;
    }

    try {
      const token = await this.tokenManager.getToken();
      if (!token) {
        console.warn('SyntaxConfigurationService: Not authenticated, using defaults');
        this.initialized = true;
        return;
      }

      const fingerprint = await this.deviceFingerprint.generate();
      const response = await this.apiService.get<{ syntax: SyntaxConfig }>('/settings/syntax', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-device-fingerprint': fingerprint,
        },
      });

      if (response.success && response.data?.syntax) {
        this.config = {
          rangeOperator: response.data.syntax.rangeOperator || DEFAULT_SYNTAX.rangeOperator,
          wildcardChar: response.data.syntax.wildcardChar || DEFAULT_SYNTAX.wildcardChar,
          addOperator: response.data.syntax.addOperator || DEFAULT_SYNTAX.addOperator,
          excludeOperator: response.data.syntax.excludeOperator || DEFAULT_SYNTAX.excludeOperator,
        };
        debugLog('SyntaxConfigurationService: Loaded config', this.config);
      }

      this.lastFetchTime = Date.now();
    } catch (error) {
      console.warn('SyntaxConfigurationService: Failed to fetch syntax config, using defaults', error);
    }

    this.initialized = true;
  }

  getConfig(): SyntaxConfig {
    return { ...this.config };
  }

  getRangeOperator(): string {
    return this.config.rangeOperator;
  }

  getWildcardChar(): string {
    return this.config.wildcardChar;
  }

  getAddOperator(): string {
    return this.config.addOperator;
  }

  getExcludeOperator(): string {
    return this.config.excludeOperator;
  }

  isRange(value: string): boolean {
    return value.includes(this.config.rangeOperator);
  }

  parseRange(value: string): [string, string] | null {
    if (!this.isRange(value)) {
      return null;
    }
    const parts = value.split(this.config.rangeOperator);
    if (parts.length !== 2) {
      return null;
    }
    return [parts[0].trim(), parts[1].trim()];
  }

  isWildcard(value: string): boolean {
    return value.includes(this.config.wildcardChar);
  }

  patternToRegex(pattern: string): RegExp {
    const regexStr = pattern
      .replace(/x/gi, '\\d')
      .replace(new RegExp(`\\${this.config.wildcardChar}`, 'g'), '\\d');

    return new RegExp(`^${regexStr}$`, 'i');
  }

  invalidateCache(): void {
    this.lastFetchTime = 0;
    debugLog('SyntaxConfigurationService: Cache invalidated');
  }
}

export const syntaxConfig = SyntaxConfigurationService.getInstance();
